import { useGetVersionsQuery, Version } from '@/__generated__/graphql';
import { NOT_APPLICABLE_LABEL } from '@/constants';
import { useGetStructureId } from '@/hooks/useGetStructureId';
import * as state from '@/state';
import { abyssColors, WorkflowStageColors } from '@/theme/colors';
import { convertTimeStampToDate } from '@/utils';
import { ChevronRight, SmartToy } from '@mui/icons-material';
import { Avatar, Stack, Typography } from '@mui/material';
import { useCallback, useMemo, useState } from 'react';
import { useAtomValue } from 'jotai';
import { Dot, Status, StatusChip, TimeLine } from './styles';

type StageInfo = {
  name: string;
  color: {
    fill: string;
    color: string;
    backgroundColor: string;
  };
};

const StatusPill = ({ data }: { data: StageInfo }) => {
  return (
    <StatusChip sx={{ backgroundColor: data.color.backgroundColor }}>
      <Status color={data.color?.color}>{data.name}</Status>
    </StatusChip>
  );
};

const UserAvatar = ({
  version,
  variant = 'user',
}: {
  version: Version;
  variant?: 'assignee' | 'user';
}) => {
  return (
    <Avatar
      sx={{
        width: variant === 'assignee' ? '2.4rem' : '3.2rem',
        height: variant === 'assignee' ? '2.4rem' : '3.2rem',
      }}
    >
      {version.lastModifiedByUser?.picture && version.lastModifiedBy !== 'CLI' && (
        <img width="100%" height="100%" src={version.lastModifiedByUser?.picture ?? ''} />
      )}
      {!version.lastModifiedByUser?.picture && version.lastModifiedBy === 'CLI' && (
        <SmartToy sx={{ fontSize: variant === 'assignee' ? '1.2rem' : '1.6rem' }} />
      )}
    </Avatar>
  );
};

const StageChange = ({
  previousStage,
  nextStage,
}: {
  previousStage: StageInfo;
  nextStage: StageInfo;
}) => {
  return (
    <Stack direction="row" alignItems="center" marginLeft="3.2rem">
      <Stack width="fit-content" direction="row" gap="1.6rem" alignItems="center">
        <StatusPill data={previousStage} />
        <ChevronRight sx={{ fontSize: '1.6rem', color: abyssColors.primary[500] }} />
        <StatusPill data={nextStage} />
      </Stack>
    </Stack>
  );
};

// const AssigneeChange = ({
//   previousAssignee,
//   nextAssignee,
// }: {
//   previousAssignee: string;
//   nextAssignee: string;
// }) => {
//   return (
//     <Stack direction="row" alignItems="center" marginLeft="3.2rem">
//       <Stack width="fit-content" direction="row" gap="1.6rem" alignItems="center">
//         <UserAvatar version={previousAssignee} variant="assignee" />
//         <ChevronRight sx={{ fontSize: '1.6rem', color: abyssColors.primary[500] }} />
//         <UserAvatar version={nextAssignee} variant="assignee" />
//       </Stack>
//     </Stack>
//   );
// };

const EventUpdate = ({
  version,
  previousStage,
  nextStage,
  date,
  isAssigneeChange,
}: {
  version: Version;
  previousStage: StageInfo;
  nextStage: StageInfo;
  date: string;
  isAssigneeChange?: boolean;
}) => {
  return (
    <Stack mb="3.2rem" gap="1.2rem">
      <Stack direction="row" alignItems="center" gap="2.4rem">
        <Dot contained />
        <Stack direction="row" alignItems="center" gap={1}>
          <UserAvatar version={version} />
          <Stack gap="0.4rem">
            <Typography
              lineHeight="normal"
              fontSize="1.2rem"
              fontWeight={400}
              color={abyssColors.primary[500]}
            >
              {version.lastModifiedByUser?.name ?? 'Abyss Bot'} updated the stage
            </Typography>
            <Typography lineHeight="normal" fontSize="1.2rem" color={abyssColors.primary[400]}>
              {date}
            </Typography>
          </Stack>
        </Stack>
      </Stack>
      {!isAssigneeChange && <StageChange previousStage={previousStage} nextStage={nextStage} />}
    </Stack>
  );
};

const ZoneEvent = ({ version }: { version: Version }) => {
  const stages = useAtomValue(state.workflowStages);
  const date = convertTimeStampToDate(version.createdAt ?? '');

  const formattedDate = date
    ? `${date.getDate()} ${date.toLocaleString('default', {
        month: 'short',
      })} ${date.getFullYear()} at ${String(date.getHours()).padStart(2, '0')}:${String(
        date.getMinutes()
      ).padStart(2, '0')}`
    : NOT_APPLICABLE_LABEL;

  const validStageChangeVersions = new Set([
    'Moved to Next Workflow Stage',
    'Reverted to Previous Workflow Stage',
  ]);

  const hasAssigneeChanged = useMemo(() => {
    return Object.keys(version.updatedData).includes('workflowStatus.assigneeId');
  }, [version.updatedData]);

  const getStageInfo = useCallback(
    (data: Record<string, unknown>, key: unknown): StageInfo => {
      if (
        !data ||
        typeof data !== 'object' ||
        !Object.keys(data).includes(`workflowStatus.${key}`)
      ) {
        return { name: 'No Stage', color: WorkflowStageColors[0] };
      }

      const stageId = data[`workflowStatus.${key}`];
      const stage = stages.find((stage) => stage.id === stageId);

      if (stage) {
        const colorIndex = stage.orderInWorkflow || 0;
        return {
          name: stage.name,
          color: WorkflowStageColors[colorIndex] || WorkflowStageColors[0],
        };
      }

      return { name: 'Unknown Stage', color: WorkflowStageColors[0] };
    },
    [stages]
  );

  const previousStage = useMemo(
    () => getStageInfo(version.previousData, 'workflowStageId'),
    [getStageInfo, version.previousData]
  );

  const nextStage = useMemo(
    () => getStageInfo(version.updatedData, 'workflowStageId'),
    [getStageInfo, version.updatedData]
  );

  return (
    <>
      {validStageChangeVersions.has(version.lastModifiedFor) && (
        <EventUpdate
          version={version}
          previousStage={previousStage}
          nextStage={nextStage}
          date={formattedDate}
        />
      )}
      {hasAssigneeChanged && (
        <EventUpdate
          version={version}
          previousStage={previousStage}
          nextStage={nextStage}
          date={formattedDate}
        />
      )}
    </>
  );
};

export const ZoneHistory = () => {
  const [zoneVersions, setZoneVersions] = useState<Version[]>([]);
  const selectedZone = useAtomValue(state.selectedZoneDetails);
  const structureId = useGetStructureId();

  const { loading } = useGetVersionsQuery({
    variables: {
      input: {
        structureId,
        collectionName: 'Zone',
        referenceId: selectedZone?.id ?? '',
      },
    },
    skip: !selectedZone,
    fetchPolicy: 'no-cache',
    onCompleted: (data) => {
      if (data?.allVersions) {
        setZoneVersions(
          data.allVersions.filter(
            (version) =>
              version.lastModifiedBy !== 'CLI' &&
              version.lastModifiedFor !== 'Update Workflow Status'
          )
        );
      }
    },
  });

  if (loading) {
    return <></>;
  }

  return (
    <Stack paddingTop="3.2rem">
      <TimeLine />
      {zoneVersions.map((version) => (
        <ZoneEvent key={version.id} version={version} />
      ))}
    </Stack>
  );
};
